var m = require('mithril');
var prop = require('mithril/stream');
var loaders = require('../layout/components/loaders');
var buttonElement = require('../components/button');
var ToggleSwitch = require('../components/toggleSwitch');
var flash = require('../components/flash');
var session = require('../auth/models/session');
var choices = require('../libs/choices');
var headingElement = require('../components/heading');

var state = {
  lookbackDays: prop(''),
  notifyAlarmReceivers: prop(true),
  notifyAdminsGroup: prop(true),
  adminNotificationGroupIds: prop([]),
  alarmReceiverNotificationThreshold: prop(''),
  groups: prop([]),
  loaded: prop(false),
  loading: true,
  saving: false,
  message: prop([]),
  error: prop([])
};

function resetAlerts() {
  state.message([]);
  state.error([]);
}

function setMessage(items) {
  state.message(items);
}

function setError(items) {
  state.error(items);
}

function loadSettings() {
  return loaders.requestWithLoader({
    loader: 'main',
    method: 'GET',
    url: '/api/v1/audit/settings',
  })
  .then(function (res) {
    if (!res || typeof res !== 'object') {
      setError(['Failed to load audit settings']);
      return false;
    }
    state.lookbackDays(res.lookbackDays != null ? String(res.lookbackDays) : '');
    state.notifyAlarmReceivers(!!res.notifyAlarmReceivers);
    state.notifyAdminsGroup(!!res.notifyAdminsGroup);
    state.adminNotificationGroupIds(Array.isArray(res.adminNotificationGroupIds) ? res.adminNotificationGroupIds.slice() : []);
    state.alarmReceiverNotificationThreshold(
      res.alarmReceiverNotificationThreshold != null ? String(res.alarmReceiverNotificationThreshold) : ''
    );
    return true;
  })
  .catch(function (err) {
    const msg = (err && err.message) ? err.message : (typeof err === 'string' ? err : 'Failed to load audit settings');
    setError([msg]);
    return false;
  });
}

function loadGroups() {
  return session.listGroups()
    .then(function (groups) {
      var list = Array.isArray(groups) ? groups.slice() : [];
      list.sort(function (a, b) {
        var nameA = (a && a.name) ? String(a.name) : '';
        var nameB = (b && b.name) ? String(b.name) : '';
        return nameA.localeCompare(nameB);
      });
      var choicesData = list
        .filter(function (group) { return group && group.name !== 'Admins'; })
        .map(function (group) {
          return { key: group.name || group['@rid'], value: group['@rid'] };
        });
      state.groups(choicesData);
      return true;
    })
    .catch(function (err) {
      const msg = (err && err.message) ? err.message : (typeof err === 'string' ? err : 'Failed to load groups');
      setError([msg]);
      return false;
    });
}

function loadAll() {
  resetAlerts();
  state.loaded(false);
  state.loading = true;
  return Promise.all([loadSettings(), loadGroups()])
    .then(function (results) {
      if (results[0]) {
        state.loaded(true);
      }
    })
    .finally(function () {
      state.loading = false;
    });
}

function saveSettings() {
  resetAlerts();
  state.saving = true;
  var lookbackValue = parseInt(state.lookbackDays(), 10);
  var normalizedLookback = Number.isFinite(lookbackValue) && lookbackValue > 0
    ? lookbackValue
    : null;
  var payload = {
    lookbackDays: normalizedLookback,
    notifyAlarmReceivers: !!state.notifyAlarmReceivers(),
    notifyAdminsGroup: !!state.notifyAdminsGroup(),
    adminNotificationGroupIds: (state.adminNotificationGroupIds() || []).slice(),
    alarmReceiverNotificationThreshold: (function () {
      var value = parseInt(state.alarmReceiverNotificationThreshold(), 10);
      return Number.isFinite(value) && value > 0 ? value : null;
    })()
  };
  return loaders.requestWithLoader({
    loader: 'submit',
    method: 'PUT',
    url: '/api/v1/audit/settings',
    body: payload
  })
  .then(function (res) {
    if (res && typeof res === 'object') {
      state.lookbackDays(res.lookbackDays != null ? String(res.lookbackDays) : state.lookbackDays());
      state.notifyAlarmReceivers(!!res.notifyAlarmReceivers);
      state.notifyAdminsGroup(!!res.notifyAdminsGroup);
      state.adminNotificationGroupIds(Array.isArray(res.adminNotificationGroupIds) ? res.adminNotificationGroupIds.slice() : []);
      state.alarmReceiverNotificationThreshold(
        res.alarmReceiverNotificationThreshold != null
          ? String(res.alarmReceiverNotificationThreshold)
          : state.alarmReceiverNotificationThreshold()
      );
    }
    setMessage(['Saved audit settings']);
  })
  .catch(function (err) {
    const msg = (err && err.message) ? err.message : (typeof err === 'string' ? err : 'Failed to save audit settings');
    setError([msg]);
  })
  .finally(function () {
    state.saving = false;
  });
}

var auditsSettings = {
  oninit: function () {
    return loadAll();
  },
  view: function () {
    var content = null;
    if (state.loading) {
      content = m('div.tc', [
        m('img.h1', { src: '/images/loading-bars.svg' })
      ]);
    } else if (state.loaded()) {
      var notifyAlarmReceivers = !!state.notifyAlarmReceivers();
      var notifyAdminsGroup = !!state.notifyAdminsGroup();
      var groupChoices = state.groups();

      content = m('div', [
        m('div.mb3', [
          m('label.db', 'Audit Lookback (days)'),
          m('input.form-control.w-25-ns', {
            type: 'number',
            min: 1,
            step: 1,
            autocomplete: 'off',
            value: state.lookbackDays(),
            oninput: function (e) {
              state.lookbackDays(e.target.value);
            }
          }),
          m('div.f6.gray.mt1', 'Controls how far back audit queries look for offline data.')
        ]),
        m('div.mb3', [
          m('div.flex.items-center.justify-between', [
            m('span', 'Notify Alarm Receivers'),
            m(ToggleSwitch, {
              id: 'audit-notify-alarm-receivers-toggle',
              checked: notifyAlarmReceivers,
              ariaLabel: 'Notify alarm receivers',
              onchange: function () {
                state.notifyAlarmReceivers(!notifyAlarmReceivers);
              }
            })
          ]),
          m('div.f6.gray.mt1', 'Controls email/SMS notifications for alarm receivers.')
        ]),
        m('div.mb3', [
          m('label.db', 'Alarm Receiver Notification Threshold'),
          m('input.form-control.w-25-ns', {
            type: 'number',
            min: 1,
            step: 1,
            autocomplete: 'off',
            value: state.alarmReceiverNotificationThreshold(),
            oninput: function (e) {
              state.alarmReceiverNotificationThreshold(e.target.value);
            }
          }),
          m('div.f6.gray.mt1', 'If newly offline resources in a single audit run exceeds this value, alarm receivers are not notified.'),
          m('div.f6.gray.mt1', 'Leave blank or 0 for no limit.')
        ]),
        m('div.mb3', [
          m('div.flex.items-center.justify-between', [
            m('span', 'Notify Admins Group'),
            m(ToggleSwitch, {
              id: 'audit-notify-admins-toggle',
              checked: notifyAdminsGroup,
              ariaLabel: 'Notify admins group',
              onchange: function () {
                state.notifyAdminsGroup(!notifyAdminsGroup);
              }
            })
          ]),
          m('div.f6.gray.mt1', 'Controls notifications sent to the Admins group.')
        ]),
        m(headingElement, { label: 'Additional Admin Notification Groups' }),
        m('div.mb3', [
          groupChoices.length ? m('div.mb2', [
            m(choices, {
              data: groupChoices,
              value: state.adminNotificationGroupIds,
            })
          ]) : m('div.f6.gray.mt1', 'No groups available.'),
          m('div.f6.gray.mt1', 'Admins group is configured separately above.')
        ]),
        m('div.mt2.flex.justify-end', [
          m(buttonElement, {
            label: state.saving ? 'Saving...' : 'Save',
            class: 'green',
            style: { minWidth: '33%' },
            func: saveSettings
          })
        ])
      ]);
    }
    return m('div', [
      m('h3.mt0', 'Audits'),
      state.error() && state.error().length ? m(flash, { items: state.error(), bg: 'bg-red' }) : null,
      state.message() && state.message().length ? m(flash, { items: state.message(), bg: 'bg-green' }) : null,
      content
    ]);
  }
};

module.exports = auditsSettings;
